// ─────────────────────────────────────────────────────────────────────────────
// Bridge from the PWA to the FlowFocus browser extension's website blocker.
//
// A web page cannot block other sites — only the extension can (via
// declarativeNetRequest). So when a focus session runs here, we ask the
// extension to engage its blocker. The extension injects a content script on
// this origin that relays our window messages to its background worker, which
// means the PWA never needs to know the extension's id.
//
// If the extension isn't installed, these messages simply go unheard — the app
// keeps working, just without blocking.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useSyncExternalStore } from 'react';

const PWA_SOURCE = 'flowfocus-pwa';
const EXT_SOURCE = 'flowfocus-ext';

let connected = false;
const listeners = new Set<() => void>();
function emit() { listeners.forEach((l) => l()); }

function post(msg: Record<string, unknown>) {
  window.postMessage({ source: PWA_SOURCE, ...msg }, window.location.origin);
}

let started = false;
/** Register the extension handshake listener and ping for its presence. */
function initBlockerBridge() {
  if (started) return;
  started = true;
  window.addEventListener('message', (e) => {
    if (e.source !== window) return;
    const d = e.data;
    if (!d || d.source !== EXT_SOURCE) return;
    if (d.type === 'READY' && !connected) { connected = true; emit(); }
  });
  // The content script may load before or after us — ping so it (re)announces.
  post({ type: 'PING' });
}

/** Watches the focus state and tells the extension to block / unblock. */
export function useBlockerBridge(focusing: boolean, endsAt?: number) {
  useEffect(() => { initBlockerBridge(); }, []);
  const last = useRef<{ focusing: boolean; endsAt?: number }>({ focusing: false });
  useEffect(() => {
    const changed = focusing !== last.current.focusing || (focusing && endsAt !== last.current.endsAt);
    if (!changed) return;
    if (focusing) post({ type: 'FOCUS_START', endsAt });
    else post({ type: 'FOCUS_STOP' });
    last.current = { focusing, endsAt };
  }, [focusing, endsAt]);
}

/** True once the extension's content script has announced itself. */
export function useExtensionConnected(): boolean {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; },
    () => connected,
    () => false,
  );
}
